import { ARS } from "aic";
import { eHierarchyLevel } from "../../functions/api/enums";
import { HierarchyReadDto } from "../../functions/api/hierarchy/HierarchyReadDto";
import { getThisAndParentHierarchies } from "../../functions/getParentHierarchies";
import { HierarchyParentTemplates } from "./interface";
import { mergeClinicalLibraryData } from "./mergeClinicalLibraryData";

const templateLevels: (eHierarchyLevel | undefined)[] = [
  eHierarchyLevel.TopLevel,
  eHierarchyLevel.Corporate,
  eHierarchyLevel.TA,
  eHierarchyLevel.Project,
];

/**
 * Determines whether a hierarchy level is a template or shell.
 *
 * @param level - The hierarchy level to check
 * @returns "Template" if the level is a template level, otherwise "Shell"
 */
const levelType = (level: eHierarchyLevel) => {
  return templateLevels.includes(level) ? "Template" : "Shell";
};

/**
 * Builds the parent templates for a given hierarchy node from the available hierarchy nodes.
 * Collects domain replacements, lists, row groups, singles, and clinical library data
 * from all parent hierarchies, deduplicating by name/key where appropriate.
 *
 * @param currentHierarchy - The hierarchy node for which to build parent templates
 * @param hierarchyNodes - All available hierarchy nodes to search for parents
 * @returns The parent templates object containing all inherited replacements and library data
 */
export const hcr_buildParentTemplates = (
  currentHierarchy: HierarchyReadDto,
  hierarchyNodes: HierarchyReadDto[],
): HierarchyParentTemplates => {
  const parents = getThisAndParentHierarchies(currentHierarchy.parentId, hierarchyNodes);
  const parentsWithLevelType = parents.map((ph) => ({
    ...ph,
    levelType: templateLevels.includes(ph.level) ? "Template" : "Shell",
  }));

  return parentsWithLevelType.reduce<HierarchyParentTemplates>(
    (acc, ph) => {
      // Add in new domain replacements from this hierarchy
      if (levelType(currentHierarchy.level) === ph.levelType) {
        acc.domainReplacements = [
          ...acc.domainReplacements,
          ...ph.domainReplacements.filter(
            (dr) =>
              !acc.domainReplacements.some(
                (r) => r.domain === dr.domain && r.oldText === dr.oldText,
              ),
          ),
        ];
      }

      // Add in new external list replacements from this hierarchy
      if (levelType(currentHierarchy.level) === ph.levelType) {
        acc.lists = [
          ...acc.lists,
          ...ph.lists
            .filter(
              (list) =>
                !acc.lists.some(
                  (l) =>
                    l.givenName ===
                    (list.givenName ??
                      // istanbul ignore next
                      "blank"),
                ),
            )
            .map((list) => ({ ...list, fromHierarchyId: ph.id })),
        ];
      }

      // Add in new row groups from this hierarchy - no level type filter
      acc.rowGroups = [
        ...acc.rowGroups,
        ...ph.rowGroups
          .filter(
            (rowGroup) =>
              !acc.rowGroups.some(
                (rg) =>
                  rg.name ===
                  (rowGroup.name ??
                    // istanbul ignore next
                    "blank"),
              ),
          )
          .map((rg) => ({ ...rg, fromHierarchyId: ph.id })),
      ];

      // Get current single names
      if (levelType(currentHierarchy.level) === ph.levelType) {
        acc.singles = [
          ...acc.singles,
          ...ph.singles
            .filter((single) => !acc.singles.some((s) => s.oldText === single.oldText))
            .map((single) => ({ ...single, fromHierarchyId: ph.id })),
        ];
      }

      // Add clinical library data from parent hierarchies with matching level type
      if (levelType(currentHierarchy.level) === ph.levelType && ph.clinicalLibrary) {
        const mergedClinicalData = mergeClinicalLibraryData(
          {
            clinicalDatasets: acc.clinicalDatasets,
            clinicalCodeLists: acc.clinicalCodeLists,
            clinicalValueLists: acc.clinicalValueLists,
            clinicalComments: acc.clinicalComments,
            clinicalMethods: acc.clinicalMethods,
            clinicalWhereClauses: acc.clinicalWhereClauses,
          },
          ph.clinicalLibrary,
        );
        return {
          ...acc,
          ...mergedClinicalData,
        };
      }

      // Add ARS entities from parent hierarchies - no level type filter
      acc.analysisSets = [
        ...acc.analysisSets,
        ...(ph.analysisSets ?? [])
          .filter((ans) => !acc.analysisSets.some((a) => a.name === ans.name))
          .map((as) => ({ ...as, fromHierarchyId: ph.id })),
      ];

      acc.analysisMethods = [
        ...acc.analysisMethods,
        ...(ph.analysisMethods ?? [])
          .filter((am) => !acc.analysisMethods.some((a) => a.name === am.name))
          .map((am) => ({ ...am, fromHierarchyId: ph.id })),
      ];

      acc.dataSubsets = [
        ...acc.dataSubsets,
        ...(ph.dataSubsets ?? [])
          .filter(
            (ds) =>
              !acc.dataSubsets.some(
                (d) => (d as ARS.DataSubset).name === (ds as ARS.DataSubset).name,
              ),
          )
          .map((ds) => ({ ...ds, fromHierarchyId: ph.id })),
      ];

      acc.groupingFactors = [
        ...acc.groupingFactors,
        ...(ph.groupingFactors ?? [])
          .filter(
            (gf) =>
              !acc.groupingFactors.some(
                (g) => (g as ARS.GroupingFactor).name === (gf as ARS.GroupingFactor).name,
              ),
          )
          .map((gf) => ({ ...gf, fromHierarchyId: ph.id })),
      ];

      return acc;
    },
    {
      clinicalDatasets: [],
      clinicalCodeLists: [],
      clinicalValueLists: [],
      clinicalComments: [],
      clinicalMethods: [],
      clinicalWhereClauses: [],
      domainReplacements: [],
      lists: [],
      rowGroups: [],
      singles: [],
      analysisSets: [],
      analysisMethods: [],
      dataSubsets: [],
      groupingFactors: [],
    } as HierarchyParentTemplates,
  );
};
